import type { MutableRefObject } from "react";
import { useState } from "react";

import type { StudioState, SupportedLang } from "@acme/validators";

interface UseSessionActionsProps {
  currentSessionId: string | null;
  dirty: boolean;
  userSession: { status: "authenticated" | "loading" | "unauthenticated" };
  lang: SupportedLang;
  selectedLanguage: MutableRefObject<SupportedLang>;
  resetState: (lang: SupportedLang) => void;
  setCurrentSessionId: (id: string | null) => void;
  getCurrentState: () => StudioState;
  createSession: (
    name: string,
    lang: SupportedLang,
    state: StudioState,
  ) => Promise<void>;
  updateSession: (id: string, data: { state: StudioState }) => Promise<void>;
  deleteSession: (id: string) => Promise<void>;
  setOpen: (open: boolean) => void;
}

export function useSessionActions({
  currentSessionId,
  dirty,
  userSession,
  lang,
  selectedLanguage,
  resetState,
  setCurrentSessionId,
  getCurrentState,
  createSession,
  updateSession,
  deleteSession,
  setOpen,
}: UseSessionActionsProps) {
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isConfirmNewSessionOpen, setIsConfirmNewSessionOpen] = useState(false);
  const [isConfirmDeleteOpen, setIsConfirmDeleteOpen] = useState(false);
  const [sessionToDelete, setSessionToDelete] = useState<string | null>(null);
  const [sessionToLoad, setSessionToLoad] = useState<{
    id: string;
    state: Partial<StudioState>;
  } | null>(null);

  const startNewSession = () => {
    resetState(selectedLanguage.current);
    setCurrentSessionId(null);
    setIsConfirmNewSessionOpen(false);
  };

  const handleNewSessionClick = (selectedLang?: SupportedLang) => {
    if (selectedLang) {
      selectedLanguage.current = selectedLang;
    }
    if (!currentSessionId && dirty && userSession.status === "authenticated") {
      setIsConfirmNewSessionOpen(true);
    } else {
      startNewSession();
    }
  };

  const handleSaveSession = async () => {
    if (currentSessionId) {
      await updateSession(currentSessionId, {
        state: getCurrentState(),
      });
    } else {
      setIsCreateDialogOpen(true);
    }
  };

  const handleCreateSession = async (values: { name: string }) => {
    await createSession(values.name, lang, getCurrentState());
    setIsCreateDialogOpen(false);
  };

  const handleDeleteSession = async () => {
    if (!sessionToDelete) return;

    await deleteSession(sessionToDelete);
    setSessionToDelete(null);
    setIsConfirmDeleteOpen(false);
  };

  const handleConfirmDeleteSession = () => {
    if (currentSessionId) {
      setSessionToDelete(currentSessionId);
      setIsConfirmDeleteOpen(true);
    }
  };

  const handleItemClick = async (
    action: () => Promise<void> | void,
    actionType?: string,
  ) => {
    if (actionType) {
      setActionLoading(actionType);
    }

    try {
      await action();
    } finally {
      setActionLoading(null);
      setOpen(false);
    }
  };

  return {
    actionLoading,
    isCreateDialogOpen,
    setIsCreateDialogOpen,
    isConfirmNewSessionOpen,
    setIsConfirmNewSessionOpen,
    isConfirmDeleteOpen,
    setIsConfirmDeleteOpen,
    sessionToDelete,
    setSessionToDelete,
    sessionToLoad,
    setSessionToLoad,
    handleNewSessionClick,
    handleSaveSession,
    handleCreateSession,
    handleDeleteSession,
    handleConfirmDeleteSession,
    handleItemClick,
  };
}
